type SocketDiagnosticEvent = 'connect_error' | 'reconnect_error';

type SocketDiagnosticSnapshot = Readonly<{
    websocketUrl: string;
    path: string | undefined;
    configuredTransports: readonly string[] | undefined;
    timeoutMs: number | undefined;
    socketConnected: boolean;
    socketId: string | undefined;
    managerReadyState: string | undefined;
    activeTransport: string | undefined;
    reconnectAttempt: number | null;
}>;

type SocketDiagnosticPayload = Readonly<{
    message: string;
    context: Record<string, unknown>;
}>;

type NormalizedDiagnosticObject = Readonly<{
    constructorName?: string;
    type?: string;
    name?: string;
    method?: string;
    url?: string;
    readyState?: number;
    status?: number;
    statusCode?: number;
    statusText?: string;
    description?: string | number;
    message?: string;
    code?: string | number;
}>;

type NormalizedSocketError = Readonly<{
    name: string;
    message: string;
    type: string | null;
    code: string | number | null;
    description: string | number | boolean | NormalizedDiagnosticObject | null;
    context: NormalizedDiagnosticObject | null;
    data: string | number | boolean | Record<string, unknown> | null;
    httpStatus: number | null;
}>;

type SocketDiagnosticCause = Readonly<{
    code: string;
    summary: string;
}>;

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

const isObjectLike = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isPrimitiveDiagnosticValue = (
    value: unknown,
): value is string | number | boolean | null | undefined | bigint | symbol => {
    const valueType = typeof value;
    return (
        value === null ||
        valueType === 'string' ||
        valueType === 'number' ||
        valueType === 'boolean' ||
        valueType === 'bigint' ||
        valueType === 'symbol' ||
        value === undefined
    );
};

const toStringValue = (value: unknown): string | null => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? trimmed : null;
    }

    if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint' ||
        typeof value === 'symbol'
    ) {
        return String(value);
    }

    return null;
};

const toNumberValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const pickDiagnosticKeys = (value: Record<string, unknown>): NormalizedDiagnosticObject | null => {
    const candidate: NormalizedDiagnosticObject = {
        constructorName:
            isObjectRecord(value.constructor) || typeof value.constructor === 'function'
                ? typeof value.constructor === 'function'
                    ? value.constructor.name || undefined
                    : undefined
                : undefined,
        type: toStringValue(value.type) ?? undefined,
        name: toStringValue(value.name) ?? undefined,
        method: toStringValue(value.method) ?? undefined,
        url: toStringValue(value.url) ?? undefined,
        readyState: toNumberValue(value.readyState) ?? undefined,
        status: toNumberValue(value.status) ?? undefined,
        statusCode: toNumberValue(value.statusCode) ?? undefined,
        statusText: toStringValue(value.statusText) ?? undefined,
        description: (() => {
            const numeric = toNumberValue(value.description);
            if (numeric !== null) {
                return numeric;
            }

            return toStringValue(value.description) ?? undefined;
        })(),
        message: toStringValue(value.message) ?? undefined,
        code: (() => {
            if (typeof value.code === 'string' || typeof value.code === 'number') {
                return value.code;
            }

            return undefined;
        })(),
    };

    return Object.values(candidate).some((entry) => entry !== undefined) ? candidate : null;
};

const normalizeUnknownObject = (value: unknown): NormalizedDiagnosticObject | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    if (value instanceof Error) {
        return pickDiagnosticKeys({
            name: value.name,
            message: value.message,
        });
    }

    if (typeof ProgressEvent !== 'undefined' && value instanceof ProgressEvent) {
        return {
            constructorName: value.constructor.name,
            type: value.type,
        };
    }

    if (!isObjectRecord(value)) {
        return {
            constructorName: value.constructor?.name || 'Object',
        };
    }

    const normalized = pickDiagnosticKeys(value);
    if (normalized) {
        return normalized;
    }

    return {
        constructorName: value.constructor?.name || 'Object',
    };
};

const normalizeErrorData = (value: unknown): string | number | boolean | Record<string, unknown> | null => {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (!isObjectRecord(value)) {
        return null;
    }

    const summarized: Record<string, unknown> = {};
    for (const key of ['code', 'reason', 'status', 'statusCode', 'message', 'type']) {
        const currentValue = value[key];
        if (typeof currentValue === 'string' || typeof currentValue === 'number' || typeof currentValue === 'boolean') {
            summarized[key] = currentValue;
        }
    }

    return Object.keys(summarized).length ? summarized : null;
};

const extractHttpStatus = ({
    code,
    description,
    context,
    data,
}: {
    code: string | number | null;
    description: string | number | boolean | NormalizedDiagnosticObject | null;
    context: NormalizedDiagnosticObject | null;
    data: string | number | boolean | Record<string, unknown> | null;
}): number | null => {
    const candidates: unknown[] = [code];

    if (typeof description === 'number') {
        candidates.push(description);
    } else if (description && typeof description === 'object') {
        candidates.push(description.status, description.statusCode, description.code, description.description);
    }

    if (context) {
        candidates.push(context.status, context.statusCode, context.code, context.description);
    }

    if (isObjectRecord(data)) {
        candidates.push(data.status, data.statusCode, data.code);
    }

    for (const candidate of candidates) {
        const parsed = toNumberValue(candidate);
        if (parsed !== null && parsed >= 100 && parsed <= 599) {
            return parsed;
        }
    }

    return null;
};

const normalizeSocketError = (error: unknown): NormalizedSocketError => {
    const errorRecord = isObjectLike(error) ? error : {};
    const name =
        (error instanceof Error ? error.name : undefined) ??
        (typeof errorRecord.name === 'string' ? errorRecord.name : undefined) ??
        'Error';
    const message =
        (error instanceof Error ? error.message : undefined) ??
        (typeof errorRecord.message === 'string' ? errorRecord.message : undefined) ??
        String(error ?? 'unknown');
    const type = typeof errorRecord.type === 'string' ? errorRecord.type : null;
    const code = typeof errorRecord.code === 'string' || typeof errorRecord.code === 'number' ? errorRecord.code : null;
    const rawDescription = errorRecord.description;
    let description: string | number | boolean | NormalizedDiagnosticObject | null = null;

    if (rawDescription !== undefined && rawDescription !== null) {
        if (isPrimitiveDiagnosticValue(rawDescription)) {
            description =
                typeof rawDescription === 'bigint' || typeof rawDescription === 'symbol'
                    ? String(rawDescription)
                    : rawDescription;
        } else {
            description = normalizeUnknownObject(rawDescription);
        }
    }
    const context = normalizeUnknownObject(errorRecord.context);
    const data = normalizeErrorData(errorRecord.data);
    const httpStatus = extractHttpStatus({
        code,
        description,
        context,
        data,
    });

    return {
        name,
        message,
        type,
        code,
        description,
        context,
        data,
        httpStatus,
    };
};

const isLoopbackHostname = (hostname: string): boolean => {
    const normalized = hostname.trim().toLowerCase();
    return LOOPBACK_HOSTS.has(normalized) || normalized.startsWith('127.');
};

const getResolvedSocketOrigin = (websocketUrl: string): string | null => {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';

    if (!websocketUrl.trim()) {
        return fallbackOrigin;
    }

    try {
        return new URL(websocketUrl, fallbackOrigin).origin;
    } catch {
        return null;
    }
};

const deriveSocketCause = ({
    event,
    normalizedError,
    browserOnline,
}: {
    event: SocketDiagnosticEvent;
    normalizedError: NormalizedSocketError;
    browserOnline: boolean | null;
}): SocketDiagnosticCause => {
    const eventLabel = event === 'reconnect_error' ? 'reconnect attempt' : 'connection attempt';
    const normalizedMessage = normalizedError.message.toLowerCase();

    if (browserOnline === false) {
        return {
            code: 'browser_offline',
            summary: 'browser is offline',
        };
    }

    if (
        normalizedMessage.includes('auth token is missing or incorrect') ||
        normalizedError.httpStatus === 401 ||
        normalizedError.httpStatus === 403
    ) {
        return {
            code: normalizedError.httpStatus === 403 ? 'handshake_forbidden' : 'auth_rejected',
            summary:
                normalizedError.httpStatus === 403
                    ? 'socket handshake was forbidden by the server'
                    : 'socket auth was rejected by the server',
        };
    }

    if (normalizedMessage.includes('trying to reach a socket.io server in v2.x')) {
        return {
            code: 'socketio_version_mismatch',
            summary: 'Socket.IO client/server versions are incompatible',
        };
    }

    if (normalizedMessage.includes('xhr poll error')) {
        if (normalizedError.httpStatus === 404) {
            return {
                code: 'socket_endpoint_not_found',
                summary: 'socket.io polling endpoint was not found (HTTP 404)',
            };
        }

        if (normalizedError.httpStatus !== null && normalizedError.httpStatus >= 500) {
            return {
                code: 'socket_worker_unavailable',
                summary: `polling handshake failed with HTTP ${normalizedError.httpStatus}`,
            };
        }

        return {
            code: 'polling_transport_failed',
            summary:
                normalizedError.httpStatus !== null
                    ? `polling handshake failed with HTTP ${normalizedError.httpStatus}`
                    : 'polling transport handshake failed',
        };
    }

    if (normalizedMessage.includes('websocket error')) {
        return {
            code: 'websocket_transport_failed',
            summary: 'websocket transport failed to open',
        };
    }

    if (normalizedError.httpStatus === 404) {
        return {
            code: 'socket_endpoint_not_found',
            summary: 'socket.io endpoint was not found (HTTP 404)',
        };
    }

    if (normalizedError.httpStatus !== null && normalizedError.httpStatus >= 500) {
        return {
            code: 'socket_worker_unavailable',
            summary: `socket handshake failed with HTTP ${normalizedError.httpStatus}`,
        };
    }

    if (normalizedMessage.includes('timeout')) {
        return {
            code: event === 'reconnect_error' ? 'reconnect_timeout' : 'connect_timeout',
            summary: `${eventLabel} timed out`,
        };
    }

    if (normalizedMessage.includes('server error')) {
        return {
            code: 'socket_server_error',
            summary:
                normalizedError.httpStatus !== null
                    ? `server reported socket error HTTP ${normalizedError.httpStatus}`
                    : 'server reported a socket connection error',
        };
    }

    return {
        code: event === 'reconnect_error' ? 'reconnect_failed' : 'connect_failed',
        summary: `${eventLabel} failed`,
    };
};

export const buildSocketDiagnosticPayload = ({
    event,
    error,
    snapshot,
}: {
    event: SocketDiagnosticEvent;
    error: unknown;
    snapshot: SocketDiagnosticSnapshot;
}): SocketDiagnosticPayload => {
    const normalizedError = normalizeSocketError(error);
    const browserOnline =
        typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : null;
    const resolvedSocketOrigin = getResolvedSocketOrigin(snapshot.websocketUrl);
    const cause = deriveSocketCause({
        event,
        normalizedError,
        browserOnline,
    });
    const loopbackTarget =
        resolvedSocketOrigin !== null ? isLoopbackHostname(new URL(resolvedSocketOrigin).hostname) : null;

    return {
        message:
            event === 'reconnect_error'
                ? `[client.realtime-socket.reconnect] Socket reconnect failed: ${cause.summary}.`
                : `[client.realtime-socket.connect] Socket connection failed: ${cause.summary}.`,
        context: {
            socketEvent: event,
            causeCode: cause.code,
            causeSummary: cause.summary,
            websocketUrl: snapshot.websocketUrl || 'same-origin',
            usesSameOriginSocketProxy: snapshot.websocketUrl.trim().length === 0,
            resolvedSocketOrigin,
            resolvedSocketPath: snapshot.path ?? '/socket.io',
            isLoopbackSocketTarget: loopbackTarget,
            configuredTransports: snapshot.configuredTransports,
            timeoutMs: snapshot.timeoutMs,
            managerReadyState: snapshot.managerReadyState,
            activeTransport: snapshot.activeTransport,
            socketConnected: snapshot.socketConnected,
            socketId: snapshot.socketId ?? null,
            reconnectAttempt: snapshot.reconnectAttempt,
            browserOnline,
            visibilityState:
                typeof document !== 'undefined' && typeof document.visibilityState === 'string'
                    ? document.visibilityState
                    : null,
            pageOrigin: typeof window !== 'undefined' ? window.location.origin : null,
            pagePathname: typeof window !== 'undefined' ? window.location.pathname : null,
            errorName: normalizedError.name,
            errorMessage: normalizedError.message,
            errorType: normalizedError.type,
            errorCode: normalizedError.code,
            httpStatus: normalizedError.httpStatus,
            errorDescription: normalizedError.description,
            errorContext: normalizedError.context,
            errorData: normalizedError.data,
        },
    };
};
